import { type FormEvent, useEffect, useState, Fragment } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import { Badge, Button, Card, FormRow, Input, Table, Toggle } from '@/admin-design/_common'
import type { BadgeVariant } from '@/admin-design/_common'

// ── Section 1: Schedule + reprice rules ─────────────────────────────────────

const SCHEDULE_DEFAULTS = {
  crawl_cron: '0 */6 * * *',
  register_cron: '',
  naver_pull_cron: '0 2 * * *',
  supplier_soldout_cron: '0 3 * * *',
  price_change_pct: 30,
  margin_rate: 1.5,
  enabled: true,
  reprice_cron: '',
  reprice_dry_run: false,
  reprice_undercut_pct: 1,
  reprice_min_margin_rate: 1.2,
  reprice_assumed_shipping_fee: 3000,
  reprice_match_threshold: 0.8,
}

type ScheduleDraft = typeof SCHEDULE_DEFAULTS

function ScheduleCard() {
  const [draft, setDraft] = useState<ScheduleDraft | null>(null)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [saving, setSaving] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['schedule'],
    queryFn: () => api.schedule(),
  })

  // 최초 데이터 도착 시 한 번만 draft 초기화. 이후에는 사용자 편집 보존.
  useEffect(() => {
    if (data && draft === null) {
      setDraft({ ...SCHEDULE_DEFAULTS, ...(data as object) })
    }
  }, [data, draft])

  const set = (patch: Partial<ScheduleDraft>) =>
    setDraft((d) => ({ ...(d ?? SCHEDULE_DEFAULTS), ...patch }))

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!draft) return
    setSaving(true)
    setMsg(null)
    try {
      await api.saveSchedule(draft)
      setMsg({ ok: true, text: '저장됐습니다.' })
    } catch (err: any) {
      setMsg({ ok: false, text: err?.message ?? '저장 실패' })
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) return <Card className="shadow-sm"><div className="px-6 py-8 text-sm text-muted-foreground">로딩 중…</div></Card>
  if (!draft) return null

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <Card.Title>리프라이스 스케줄 설정</Card.Title>
        <Card.Description>리프라이스 실행 주기 및 가격 조정 규칙을 설정합니다.</Card.Description>
      </Card.Header>
      <form onSubmit={onSubmit}>
        <div className="divide-y divide-border">
          <div className="px-6 py-4">
            <FormRow label="리프라이스 주기 (cron)" help="예: 0 */4 * * * (4시간마다). 비우면 자동 실행 안 함.">
              <Input
                type="text"
                className="max-w-md font-mono"
                value={draft.reprice_cron}
                onChange={(e) => set({ reprice_cron: e.target.value })}
              />
            </FormRow>
          </div>
          <div className="px-6 py-4">
            <FormRow label="Dry-run 모드" help="활성화하면 가격을 실제로 변경하지 않고 로그만 남깁니다.">
              <Toggle
                checked={draft.reprice_dry_run}
                onChange={(v) => set({ reprice_dry_run: v })}
              />
            </FormRow>
          </div>
          <div className="px-6 py-4">
            <FormRow label="언더컷 비율 (%)" help="경쟁사 최저가보다 이 % 낮게 설정합니다 (예: 1 = 1% 낮게).">
              <Input
                type="number"
                className="w-32"
                value={draft.reprice_undercut_pct}
                step="0.1"
                min="0"
                max="50"
                onChange={(e) => set({ reprice_undercut_pct: parseFloat(e.target.value) || 0 })}
              />
            </FormRow>
          </div>
          <div className="px-6 py-4">
            <FormRow label="최소 마진율" help="이 배율 미만으로는 가격을 낮추지 않습니다 (예: 1.2 = 원가의 120%).">
              <Input
                type="number"
                className="w-32"
                value={draft.reprice_min_margin_rate}
                step="0.05"
                min="1.0"
                max="5.0"
                onChange={(e) => set({ reprice_min_margin_rate: parseFloat(e.target.value) || 1.2 })}
              />
            </FormRow>
          </div>
          <div className="px-6 py-4">
            <FormRow label="가정 배송비 (원)" help="배송비가 없는 경쟁사 비교 시 가산할 금액.">
              <Input
                type="number"
                className="w-32"
                value={draft.reprice_assumed_shipping_fee}
                step="100"
                min="0"
                onChange={(e) => set({ reprice_assumed_shipping_fee: parseInt(e.target.value) || 0 })}
              />
            </FormRow>
          </div>
          <div className="px-6 py-4">
            <FormRow label="매칭 임계값" help="네이버 검색 결과와의 상품명 유사도 기준 (0~1, 예: 0.8).">
              <Input
                type="number"
                className="w-32"
                value={draft.reprice_match_threshold}
                step="0.05"
                min="0"
                max="1"
                onChange={(e) => set({ reprice_match_threshold: parseFloat(e.target.value) || 0.8 })}
              />
            </FormRow>
          </div>
        </div>
        <Card.Footer className="flex items-center justify-between gap-3">
          <div className="text-xs">
            {msg && (
              <span className={msg.ok ? 'text-success' : 'text-destructive'}>{msg.text}</span>
            )}
          </div>
          <Button type="submit" size="sm" variant="primary" loading={saving} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </Card.Footer>
      </form>
    </Card>
  )
}

// ── Section 2: Naver Search API credentials ──────────────────────────────────

type NaverSearchDraft = {
  client_id: string
  client_secret: string
  enabled: boolean
  client_secret_set?: boolean
}

function NaverSearchCard() {
  const [draft, setDraft] = useState<NaverSearchDraft | null>(null)
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [saving, setSaving] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['naverSearchCreds'],
    queryFn: () => api.naverSearchCreds(),
  })

  useEffect(() => {
    if (data && draft === null) {
      const d = data as { client_id?: string; enabled?: boolean; client_secret_set?: boolean }
      setDraft({
        client_id: d.client_id ?? '',
        client_secret: '',
        enabled: d.enabled ?? false,
        client_secret_set: d.client_secret_set ?? false,
      })
    }
  }, [data, draft])

  const set = (patch: Partial<NaverSearchDraft>) =>
    setDraft((d) => ({ ...(d ?? { client_id: '', client_secret: '', enabled: false }), ...patch }))

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!draft) return
    setSaving(true)
    setMsg(null)
    try {
      await api.saveNaverSearchCreds({
        client_id: draft.client_id,
        client_secret: draft.client_secret,
        enabled: draft.enabled,
      })
      setMsg({ ok: true, text: '저장됐습니다.' })
      set({ client_secret: '' })
    } catch (err: any) {
      setMsg({ ok: false, text: err?.message ?? '저장 실패' })
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) return <Card className="shadow-sm"><div className="px-6 py-8 text-sm text-muted-foreground">로딩 중…</div></Card>
  if (!draft) return null

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <Card.Title>네이버 쇼핑 검색 API 키</Card.Title>
        <Card.Description>네이버 검색 API (Naver Developers)의 Client ID / Secret을 입력합니다. 커머스 API와 별개입니다.</Card.Description>
      </Card.Header>
      <form onSubmit={onSubmit}>
        <div className="divide-y divide-border">
          <div className="px-6 py-4">
            <FormRow label="Client ID">
              <Input
                type="text"
                className="max-w-md font-mono"
                value={draft.client_id}
                onChange={(e) => set({ client_id: e.target.value })}
              />
            </FormRow>
          </div>
          <div className="px-6 py-4">
            <FormRow
              label="Client Secret"
              help={draft.client_secret_set ? '비워두면 기존 값 유지' : ''}
            >
              <Input
                type="password"
                className="max-w-md font-mono"
                value={draft.client_secret}
                placeholder={draft.client_secret_set ? '(저장됨 — 변경 시에만 입력)' : ''}
                onChange={(e) => set({ client_secret: e.target.value })}
              />
            </FormRow>
          </div>
          <div className="px-6 py-4">
            <FormRow label="API 활성화">
              <Toggle
                checked={draft.enabled}
                onChange={(v) => set({ enabled: v })}
              />
            </FormRow>
          </div>
        </div>
        <Card.Footer className="flex items-center justify-between gap-3">
          <div className="text-xs">
            {msg && (
              <span className={msg.ok ? 'text-success' : 'text-destructive'}>{msg.text}</span>
            )}
          </div>
          <Button type="submit" size="sm" variant="primary" loading={saving} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </Card.Footer>
      </form>
    </Card>
  )
}

// ── Section 3: Manual trigger ────────────────────────────────────────────────

function ManualRunCard() {
  const qc = useQueryClient()
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null)

  const trigger = useMutation({
    mutationFn: () => api.createJob('reprice', {}),
    onSuccess: () => {
      setMsg({ ok: true, text: '리프라이스 작업이 큐에 추가됐습니다.' })
      qc.invalidateQueries({ queryKey: ['repriceRuns'] })
    },
    onError: (err: any) => {
      setMsg({ ok: false, text: err?.message ?? '실행 실패' })
    },
  })

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <Card.Title>수동 실행</Card.Title>
        <Card.Description>스케줄과 무관하게 즉시 리프라이스를 실행합니다.</Card.Description>
      </Card.Header>
      <div className="px-6 py-4 flex items-center gap-4">
        <Button
          variant="primary"
          size="sm"
          onClick={() => { setMsg(null); trigger.mutate() }}
          loading={trigger.isPending}
          disabled={trigger.isPending}
        >
          지금 한 번 실행
        </Button>
        {msg && (
          <span className={`text-xs ${msg.ok ? 'text-success' : 'text-destructive'}`}>
            {msg.text}
          </span>
        )}
      </div>
    </Card>
  )
}

// ── Section 4: Recent runs table ─────────────────────────────────────────────

function statusVariant(status: string): BadgeVariant {
  const map: Record<string, BadgeVariant> = {
    done: 'success',
    error: 'destructive',
    failed: 'destructive',
    running: 'info',
    queued: 'warning',
    pending: 'warning',
    cancelled: 'muted',
  }
  return map[status] ?? 'muted'
}

function RecentRunsCard() {
  const [expandedRunId, setExpandedRunId] = useState<number | null>(null)
  const [expandedObsId, setExpandedObsId] = useState<number | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['repriceRuns'],
    queryFn: () => api.repriceRuns(20),
    refetchInterval: 10000,
  })
  const runs: any[] = Array.isArray(data) ? data : []

  const obsQuery = useQuery({
    queryKey: ['repriceObservations', expandedRunId],
    queryFn: () => api.repriceObservations({ job_id: expandedRunId!, limit: 500 }),
    enabled: expandedRunId !== null,
  })

  const toggleRun = (id: number) => {
    setExpandedRunId(prev => prev === id ? null : id)
    setExpandedObsId(null)
  }

  return (
    <Card className="shadow-sm overflow-hidden overflow-x-auto">
      <Card.Header className="py-2 px-4">
        <Card.Title className="text-sm">최근 실행 기록</Card.Title>
      </Card.Header>
      {isLoading ? (
        <div className="px-6 py-8 text-sm text-muted-foreground">로딩 중…</div>
      ) : (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.Header>Job ID</Table.Header>
              <Table.Header>Status</Table.Header>
              <Table.Header>Created</Table.Header>
              <Table.Header className="text-right">Applied</Table.Header>
              <Table.Header className="text-right">Skipped</Table.Header>
              <Table.Header className="text-right">NoMatch</Table.Header>
              <Table.Header className="text-right">APIError</Table.Header>
              <Table.Header>Error</Table.Header>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {runs.map((r: any) => (
              <Fragment key={r.id}>
                <Table.Row
                  onClick={() => toggleRun(r.id)}
                  className="cursor-pointer hover:bg-muted/30"
                >
                  <Table.Cell className="font-mono text-xs">#{r.id}</Table.Cell>
                  <Table.Cell>
                    <Badge variant={statusVariant(r.status)}>{r.status}</Badge>
                  </Table.Cell>
                  <Table.Cell className="text-xs text-muted-foreground">
                    {r.created_at
                      ? new Date(
                          typeof r.created_at === 'number'
                            ? r.created_at * 1000
                            : r.created_at,
                        ).toLocaleString()
                      : '—'}
                  </Table.Cell>
                  <Table.Cell className="text-xs text-right">{r.applied ?? '—'}</Table.Cell>
                  <Table.Cell className="text-xs text-right">{r.skipped ?? '—'}</Table.Cell>
                  <Table.Cell className="text-xs text-right">{r.no_match ?? '—'}</Table.Cell>
                  <Table.Cell className="text-xs text-right">{r.api_error ?? '—'}</Table.Cell>
                  <Table.Cell className="text-xs text-destructive max-w-xs truncate">
                    {r.last_error ?? ''}
                  </Table.Cell>
                </Table.Row>
                {expandedRunId === r.id && (
                  <Table.Row>
                    <Table.Cell colSpan={8} className="bg-muted/10 p-0">
                      <RunObservations
                        runId={r.id}
                        obs={obsQuery.data ?? []}
                        isLoading={obsQuery.isLoading}
                        expandedObsId={expandedObsId}
                        setExpandedObsId={setExpandedObsId}
                      />
                    </Table.Cell>
                  </Table.Row>
                )}
              </Fragment>
            ))}
            {runs.length === 0 && (
              <Table.Empty colSpan={8} title="실행 기록이 없습니다" />
            )}
          </Table.Body>
        </Table>
      )}
    </Card>
  )
}

// ── Section 5: Drilldown components ──────────────────────────────────────────

type Observation = {
  id: number;
  product_id: number;
  observed_at: string;
  our_price_before: number;
  our_price_after?: number;
  cost_price: number;
  lowest_lprice?: number;
  lowest_compare?: number;
  lowest_seller: string;
  match_count: number;
  applied: boolean;
  skip_reason: string;
  search_query: string;
  match_debug: Array<{
    title: string;
    lprice: number;
    mall: string;
    jaccard: number;
    brand_matched: boolean;
    score: number;
    reason: string;
  }>;
};

type RunObservationsProps = {
  runId: number;
  obs: Observation[];
  isLoading: boolean;
  expandedObsId: number | null;
  setExpandedObsId: (id: number | null) => void;
};

function RunObservations({ runId, obs, isLoading, expandedObsId, setExpandedObsId }: RunObservationsProps) {
  if (isLoading) {
    return <div className="px-4 py-3 text-sm text-muted-foreground">관찰 로그 로딩 중…</div>
  }
  if (obs.length === 0) {
    return <div className="px-4 py-3 text-sm text-muted-foreground">관찰 로그 없음</div>
  }
  return (
    <div className="border-t border-border bg-background">
      <div className="px-4 py-2 text-xs font-medium text-muted-foreground">
        Job #{runId} · 관찰 {obs.length}건
      </div>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.Header>상품 ID</Table.Header>
            <Table.Header>도매가 / 현재 → 신가</Table.Header>
            <Table.Header>최저(lprice / 보정)</Table.Header>
            <Table.Header>최저 셀러</Table.Header>
            <Table.Header>매칭</Table.Header>
            <Table.Header>결과</Table.Header>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {obs.map((o) => (
            <Fragment key={o.id}>
              <Table.Row
                onClick={() => setExpandedObsId(expandedObsId === o.id ? null : o.id)}
                className="cursor-pointer hover:bg-muted/20"
              >
                <Table.Cell>{o.product_id}</Table.Cell>
                <Table.Cell className="font-mono text-xs">
                  {o.cost_price.toLocaleString()} / {o.our_price_before.toLocaleString()} → {o.our_price_after != null ? o.our_price_after.toLocaleString() : '—'}
                </Table.Cell>
                <Table.Cell className="font-mono text-xs">
                  {o.lowest_lprice != null ? o.lowest_lprice.toLocaleString() : '—'}
                  {o.lowest_compare != null && (
                    <span className="text-muted-foreground"> / {o.lowest_compare.toLocaleString()}</span>
                  )}
                </Table.Cell>
                <Table.Cell className="text-xs">{o.lowest_seller || '—'}</Table.Cell>
                <Table.Cell className="text-xs">{o.match_count}</Table.Cell>
                <Table.Cell>
                  <ResultBadge applied={o.applied} skipReason={o.skip_reason} />
                </Table.Cell>
              </Table.Row>
              {expandedObsId === o.id && (
                <Table.Row>
                  <Table.Cell colSpan={6} className="bg-muted/5 p-0">
                    <ObservationDetail obs={o} />
                  </Table.Cell>
                </Table.Row>
              )}
            </Fragment>
          ))}
        </Table.Body>
      </Table>
    </div>
  )
}

function ResultBadge({ applied, skipReason }: { applied: boolean; skipReason: string }) {
  if (applied) {
    return <Badge variant="success">applied</Badge>
  }
  const variant: BadgeVariant =
    skipReason === 'no_match' ? 'warning'
    : skipReason === 'dry_run' ? 'info'
    : skipReason === 'no_decrease' ? 'muted'
    : skipReason === 'below_cost' ? 'warning'
    : skipReason === 'missing_cost' ? 'warning'
    : skipReason === 'api_error' || skipReason === 'naver_api_error' || skipReason === 'rate_limited' ? 'destructive'
    : 'muted'
  return <Badge variant={variant}>{skipReason || 'unknown'}</Badge>
}

function ObservationDetail({ obs }: { obs: Observation }) {
  return (
    <div className="px-4 py-3 space-y-3">
      <div className="text-xs">
        <div className="text-muted-foreground mb-1">검색어</div>
        <code className="block bg-muted px-2 py-1 rounded">{obs.search_query || '(empty)'}</code>
      </div>
      <div className="text-xs">
        <div className="text-muted-foreground mb-1">검색 결과 평가 (top-{obs.match_debug.length})</div>
        {obs.match_debug.length === 0 ? (
          <div className="text-muted-foreground">검색 결과 없음</div>
        ) : (
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Header className="w-10">#</Table.Header>
                <Table.Header>제목</Table.Header>
                <Table.Header className="text-right">lprice</Table.Header>
                <Table.Header>몰</Table.Header>
                <Table.Header className="text-right">jaccard</Table.Header>
                <Table.Header>brand</Table.Header>
                <Table.Header className="text-right">score</Table.Header>
                <Table.Header>판정</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {obs.match_debug.map((m, i) => (
                <Table.Row key={i} className={m.reason === 'passed' ? 'bg-success/5' : ''}>
                  <Table.Cell className="text-muted-foreground">{i + 1}</Table.Cell>
                  <Table.Cell className="text-xs max-w-md truncate" title={m.title}>{m.title}</Table.Cell>
                  <Table.Cell className="text-right font-mono text-xs">{m.lprice.toLocaleString()}</Table.Cell>
                  <Table.Cell className="text-xs">{m.mall}</Table.Cell>
                  <Table.Cell className="text-right font-mono text-xs">{m.jaccard.toFixed(3)}</Table.Cell>
                  <Table.Cell className="text-xs">{m.brand_matched ? '✓' : '×'}</Table.Cell>
                  <Table.Cell className="text-right font-mono text-xs">{m.score.toFixed(3)}</Table.Cell>
                  <Table.Cell>
                    <Badge variant={m.reason === 'passed' ? 'success' : m.reason === 'self_store' ? 'muted' : 'warning'}>
                      {m.reason}
                    </Badge>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </div>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function RepricePage() {
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Reprice</h1>
      <ScheduleCard />
      <NaverSearchCard />
      <ManualRunCard />
      <RecentRunsCard />
    </div>
  )
}
